if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();

      if (window.initLazyScript && !this.hidden) {
        window.initLazyScript(this, this.initLazySection.bind(this));
      } else {
        this.initLazySection();
      }
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.variantInventory = this.getVariantInventory();

      const productInfo = this.closest('.product-info');
      const variantPicker = productInfo ? productInfo.querySelector('variant-picker') : null;

      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      const selectedVariantId = this.dataset.selectedVariantId;

      if (!selectedVariantId) {
        this.resetInventoryBlock();
        return;
      }

      const selectedInventory = this.variantInventory.find(function (variant) {
        return String(variant.id) === String(selectedVariantId);
      });

      if (!selectedInventory) {
        this.resetInventoryBlock();
        return;
      }

      this.updateInventory(
        selectedInventory.inventory_quantity,
        selectedInventory.available,
        selectedInventory.inventory_policy,
        selectedInventory.fdh_task_made_to_order
      );
    }

    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');

      if (!dataEl) {
        return [];
      }

      try {
        return JSON.parse(dataEl.textContent);
      } catch (error) {
        console.error('Product inventory JSON error:', error);
        return [];
      }
    }

    handleVariantChange(evt) {
      if (!evt.detail || !evt.detail.variant) {
        this.resetInventoryBlock();
        return;
      }

      const selectedVariant = evt.detail.variant;

      const inventory = this.variantInventory.find(function (variant) {
        return String(variant.id) === String(selectedVariant.id);
      });

      if (!inventory) {
        this.resetInventoryBlock();
        return;
      }

      this.dataset.selectedVariantId = String(inventory.id);
      this.dataset.inventoryQuantity = String(inventory.inventory_quantity ?? 0);
      this.dataset.inventoryPolicy = String(inventory.inventory_policy || '');
      this.dataset.variantAvailable = String(inventory.available);

      this.updateInventory(
        inventory.inventory_quantity,
        inventory.available,
        inventory.inventory_policy,
        inventory.fdh_task_made_to_order
      );
    }

    resetInventoryBlock() {
      this.dataset.selectedVariantId = '';
      this.dataset.inventoryQuantity = '';
      this.dataset.inventoryPolicy = '';
      this.dataset.variantAvailable = '';
      this.dataset.inventoryLevel = '';
      this.fdhTaskMadeToOrder = false;

      this.removeAttribute('data-inventory-level');

      if (this.inventoryNotice) {
        this.inventoryNotice.innerText = '';
      }

      if (this.urgencyMessage) {
        this.urgencyMessage.innerHTML = '';
      }

      if (this.indicatorBar) {
        this.indicatorBar.hidden = true;

        const indicatorSpan = this.indicatorBar.querySelector('span');

        if (indicatorSpan) {
          indicatorSpan.style.width = '0%';
        }
      }

      this.hidden = true;
    }

    updateInventory(count, available, inventoryPolicy, taskMadeToOrder) {
      count = parseInt(count, 10);

      if (Number.isNaN(count)) {
        count = 0;
      }
      this.fdhTaskMadeToOrder = taskMadeToOrder === true && count > 0;

      let inventoryLevel = '';

      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      const shouldShowNotice =
        this.dataset.showNotice === 'always' ||
        this.dataset.showNotice === '' ||
        (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')) ||
        inventoryLevel === 'none' ||
        inventoryLevel === 'backordered' ||
        inventoryLevel === 'normal' ||
        inventoryLevel === 'in_stock';

      const shouldHideBackorder =
        inventoryLevel === 'backordered' &&
        this.dataset.showNoStockBackordered === 'false';

      if (!shouldShowNotice || shouldHideBackorder) {
        this.resetInventoryBlock();
        return;
      }

      this.hidden = false;
      this.setAttribute('data-inventory-level', inventoryLevel);
      this.dataset.inventoryLevel = inventoryLevel;

      this.updateInventoryTitle(inventoryLevel, count);
      this.updateInventoryDescription(inventoryLevel);
      this.updateInventoryBar(inventoryLevel, count);
    }

    updateInventoryTitle(inventoryLevel, count) {
      if (!this.inventoryNotice) {
        return;
      }

      if (inventoryLevel === 'backordered') {
        this.inventoryNotice.innerText =
          this.dataset.textBackorderTitle || 'Available on backorder';
        return;
      }

      if (inventoryLevel === 'none') {
        this.inventoryNotice.innerText =
          this.dataset.textNoStockTitle || 'Out of stock';
        return;
      }

      if (this.fdhTaskMadeToOrder) {
        this.inventoryNotice.innerText = `${count} available to build`;
        return;
      }

      if (
        inventoryLevel !== 'in_stock' &&
        (
          this.dataset.showCount === 'always' ||
          (this.dataset.showCount === 'low' && inventoryLevel.includes('low'))
        )
      ) {
        if (window.theme && theme.strings && theme.strings.onlyXLeft) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else {
          this.inventoryNotice.innerText = `${count} in stock`;
        }

        return;
      }

      if (inventoryLevel === 'very_low') {
        this.inventoryNotice.innerText =
          window.theme && theme.strings && theme.strings.veryLowStock
            ? theme.strings.veryLowStock
            : 'Very low stock';
        return;
      }

      if (inventoryLevel === 'low') {
        this.inventoryNotice.innerText =
          window.theme && theme.strings && theme.strings.lowStock
            ? theme.strings.lowStock
            : 'Low stock';
        return;
      }

      if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
        this.inventoryNotice.innerText =
          this.dataset.textNormalTitle ||
          (
            window.theme && theme.strings && theme.strings.inStock
              ? theme.strings.inStock
              : 'In stock'
          );
      }
    }

    updateInventoryDescription(inventoryLevel) {
      if (!this.urgencyMessage) {
        return;
      }

      if (this.fdhTaskMadeToOrder && inventoryLevel !== 'backordered' && inventoryLevel !== 'none') {
        this.urgencyMessage.textContent = 'Made to order — ships in 3–5 business days.';
        return;
      }

      let description = '';

      if (inventoryLevel === 'backordered') {
        description = this.dataset.textBackorderDescription || '';
      } else if (inventoryLevel === 'none') {
        description = this.dataset.textNoStockDescription || '';
      } else if (inventoryLevel === 'very_low') {
        description = this.dataset.textVeryLow || '';
      } else if (inventoryLevel === 'low') {
        description = this.dataset.textLow || '';
      } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
        description = this.getNormalInventoryDescription();
      }

      this.urgencyMessage.innerHTML = this.addVendorInventoryDescription(description);
    }

    getNormalInventoryDescription() {
      return this.getVendorInventoryDescription() || this.dataset.textNormalDescription || '';
    }

    getVendorInventoryDescription() {
      const vendor = this.dataset.productVendor || '';
      const normalizedVendor = vendor.trim().toLowerCase();

      if (normalizedVendor === 'jensen') {
        return this.dataset.jensenText || '';
      }
      if (normalizedVendor === 'ketcham') {
        return this.dataset.ketchamText || '';
      }
      if (normalizedVendor.includes('better home products')) {
        return this.dataset.betterHomeProductsText || '';
      }

      if (normalizedVendor.includes('air-pro')) {
        return this.dataset.airProText || '';
      }

      return '';
    }

    addVendorInventoryDescription(description) {
      const vendorDescription = this.getVendorInventoryDescription();

      if (!description) {
        return vendorDescription;
      }

      if (!vendorDescription || description === vendorDescription) {
        return description;
      }

      return `${description}<br>${vendorDescription}`;
    }

    updateInventoryBar(inventoryLevel, count) {
      if (!this.indicatorBar) {
        return;
      }

      this.indicatorBar.hidden = false;

      let newWidth = 0;
      const scale = parseInt(this.dataset.scale, 10) || 35;

      if (
        (count >= scale || inventoryLevel === 'in_stock' || inventoryLevel === 'normal') &&
        inventoryLevel !== 'backordered' &&
        inventoryLevel !== 'none'
      ) {
        newWidth = 100;
      } else {
        newWidth = Math.max(0, Math.min(100, ((100 / scale) * count))).toFixed(1);
      }

      const indicatorSpan = this.indicatorBar.querySelector('span');

      if (indicatorSpan) {
        indicatorSpan.style.width = `${newWidth}%`;
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
